import { SalesWaveChart, TimeRangeFilter, GrowthDeclineIndicator, Card } from '@bbpos/ui';
import type { TimeRangeValue } from '@bbpos/ui';
import { hourLabel } from '@bbpos/utils';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import type { SalesWaveResponse } from '@/services/dashboard.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { hourIn, useStoreTimezone } from '@/lib/store-clock';

interface SalesWaveSectionProps {
  data: SalesWaveResponse | undefined;
  loading?: boolean;
  timeRange: TimeRangeValue;
  onTimeRangeChange: (value: TimeRangeValue) => void;
}

export function SalesWaveSection({
  data,
  loading,
  timeRange,
  onTimeRangeChange,
}: SalesWaveSectionProps) {
  const currency = useTenantCurrency();
  const timeZone = useStoreTimezone(useDashboardStore((s) => s.selectedLocationId));
  // Build chart data points — hours 6AM to 23 (11PM)
  const chartData = Array.from({ length: 18 }, (_, i) => {
    const hour = i + 6;
    const todayPoint = data?.today.find((h) => h.hour === hour);
    const compPoint = data?.comparison.find((h) => h.hour === hour);
    return {
      label: hourLabel(hour),
      today: todayPoint?.revenue ?? 0,
      yesterday: compPoint?.revenue ?? 0,
    };
  });

  const isWindowMode = (data?.days ?? 1) > 1;
  // In window mode the "primary" series is an averaged curve over a range,
  // so the running-cutoff at the current hour doesn't apply — sum across
  // the full curve instead.
  // Calculate today vs yesterday difference at the current store hour.
  // The backend buckets hourly sales on the store's clock (T18: the location's
  // zone, else the business's), so we MUST compare against that hour here too
  // — `new Date().getHours()` returns the browser's local hour, which silently
  // mis-aligns the cutoff for anyone in another zone.
  const currentHour = hourIn(timeZone);
  const cutoffHour = isWindowMode ? 23 : currentHour;
  const todayTotal = data?.today
    .filter((h) => h.hour <= cutoffHour)
    .reduce((sum, h) => sum + h.revenue, 0) ?? 0;
  const compTotal = data?.comparison
    .filter((h) => h.hour <= cutoffHour)
    .reduce((sum, h) => sum + h.revenue, 0) ?? 0;
  const diff = todayTotal - compTotal;
  const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  // Suppress the comparison entirely when the comparison day had no sales —
  // otherwise the user sees "+₹X ahead" with no baseline, which is misleading.
  const hasComparison = compTotal > 0;

  const presets = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'last7', label: 'Last 7 Days' },
    { id: 'custom', label: 'Custom' },
  ];

  return (
    <Card className="p-4 lg:p-6 shadow-md backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 transition-shadow duration-200 hover:shadow-lg border border-neutral-100 dark:border-neutral-800">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white shadow-sm">
            <svg className="h-4.5 w-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
            </svg>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
              Sales Wave
            </h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {isWindowMode
                ? `Average hourly performance over ${data?.days ?? 7} days`
                : 'Intraday performance tracking'}
            </p>
          </div>
        </div>
        <TimeRangeFilter
          value={timeRange}
          onChange={onTimeRangeChange}
          presets={presets}
          loading={loading}
        />
      </div>

      <SalesWaveChart data={chartData} currency={currency} />

      {data && data.peak_hour_today !== null && (
        <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
          {isWindowMode ? 'Typical peak hour:' : 'Peak hour today:'}{' '}
          <span className="font-semibold text-neutral-700 dark:text-neutral-200">
            {hourLabel(data.peak_hour_today)}
          </span>
        </p>
      )}

      {data && hasComparison && (
        <div className="mt-3 flex items-center gap-2">
          <GrowthDeclineIndicator
            direction={direction as 'up' | 'down' | 'flat'}
            value={formatPaise(Math.abs(diff), currency)}
            label={
              isWindowMode
                ? diff >= 0
                  ? `ahead of the prior ${data?.days ?? 7} days`
                  : `behind the prior ${data?.days ?? 7} days`
                : diff >= 0
                  ? 'ahead of yesterday at this hour'
                  : 'behind yesterday at this hour'
            }
            size="sm"
            animate
          />
        </div>
      )}
    </Card>
  );
}
